import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-pro";
import { PAGE_URL } from "../../../constants";
import { crm } from "../../../store/cabinetApi/crm";
import { transformMuiDataGridFilterSortToApiModel } from "../../../utils/dataUtils";
import { AppCrmContactTasksListApiArg, ContactTask } from "../../../store/cabinetApi/generated/crm";
import { useDeepCompareEffect } from "react-use";


type TasksApiFilter = Omit<AppCrmContactTasksListApiArg, 'offset'|'limit'>;

const useTasksList = ({ pageSize, defaultFilter = {items: []}, defaultSort, search }: {
  pageSize: number; defaultFilter?: GridFilterModel; defaultSort?: GridSortModel; search?: string
}) => {
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<GridFilterModel>(defaultFilter || { items: [] });
  const [sort, setSort] = useState<GridSortModel>(
    defaultSort || [{ field: 'created_at', sort: 'desc' }]
  );

  const apiFilter: TasksApiFilter = useMemo(() => {
    return transformMuiDataGridFilterSortToApiModel({ filterModel: filter, sortModel: sort }, true);
  }, [filter, sort]);

  const { data: tasksData, isLoading: loadingTasks } = crm.useAppCrmContactTasksListQuery({
    limit: pageSize, offset: page * pageSize, search,
    ...apiFilter,
  });

  const navigate = useNavigate();

  const handleNavigateToContact = (contactId: Required<ContactTask>['contacts'][number]) => (
    navigate(`${PAGE_URL.CRM_PEOPLE}/${contactId}`)
  );

  const handleSetFilter = (fltr: GridFilterModel | undefined) => {
    setPage(0);
    setFilter(fltr || { items: [] });
  };

  const handleSetSort = (srt: GridSortModel | undefined) => {
    setPage(0);
    setSort(srt || []);
  };

  useDeepCompareEffect(() => setFilter(defaultFilter || { items: [] }), [defaultFilter]);
  // reset filter anytime search changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setFilter(defaultFilter || { items: [] }), [search]);

  return {
    tasks: tasksData?.data || [],
    rowCount: tasksData?.meta?.count || 0,
    loading: loadingTasks,
    filter,
    sort,
    page,
    pageSize,
    onNavigateToContact: handleNavigateToContact,
    onSetFilter: handleSetFilter,
    onSetSort: handleSetSort,
    onPageChange: setPage,
  };
};

export default useTasksList;
